import { useCallback, useEffect, useRef } from 'react';

/**
 * Debounce с отдельным таймером на каждый ключ.
 * При вызове schedule(key) сбрасывается таймер только для этого ключа.
 * flush() немедленно выполняет все отложенные вызовы.
 * При размонтировании — flush (сохраняем данные).
 */
export function useDebouncePerKey<TKey extends string | number>(
  delay: number,
  callback: (key: TKey) => void
): [schedule: (key: TKey) => void, flush: () => void] {
  const timersRef = useRef<Map<TKey, ReturnType<typeof setTimeout>>>(new Map());
  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  const schedule = useCallback(
    (key: TKey) => {
      const existing = timersRef.current.get(key);
      if (existing) clearTimeout(existing);
      const timer = setTimeout(() => {
        timersRef.current.delete(key);
        callbackRef.current(key);
      }, delay);
      timersRef.current.set(key, timer);
    },
    [delay]
  );

  const flush = useCallback(() => {
    timersRef.current.forEach((timer, key) => {
      clearTimeout(timer);
      callbackRef.current(key);
    });
    timersRef.current.clear();
  }, []);

  useEffect(() => {
    return () => {
      timersRef.current.forEach((timer, key) => {
        clearTimeout(timer);
        callbackRef.current(key);
      });
      timersRef.current.clear();
    };
  }, []);

  return [schedule, flush];
}
